/**
 * Custom override for ForgotPassword data hook.
 * Replaces two-page OOTB flow with a single-page multi-step flow.
 */
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNotifications } from '@/data/Content/Notifications';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { publicClient } from 'integration/generated/custom/client';
import { ContentType } from 'integration/generated/custom/http-client';
import type { ComIbmCommerceRestMemberHandlerPersonHandlerUserRegistrationUpdateRequest } from 'integration/generated/transactions/data-contracts';
import transactionsPerson from 'integration/generated/transactions/transactionsPerson';
import { GA4_AUTH_EVENTS } from 'presentation/events/constants/eventConstants';
import { GA4PasswordRecoveredPayload } from 'presentation/events/types/ga4Types';
import { NOT_APPLICABLE } from '@/data/constants/gtm';
import { useTrackAuthEvent } from 'presentation/events/hooks/useTrackAuthEvent';
import { EMPTY_STRING } from '../constants/order';
import { REGEX } from '@/data/constants/regex';

// Re-export unchanged server-side and OOTB exports from core.
export { forgotPasswordFetcher, getForgotPassword } from '@/core/data/Content/ForgotPassword';
// Kept for OOTB ResetPassword page backward compatibility.
export const initialForgotPassword = { logonId: '', email: '' };

export const validateForgotPasswordEmail = (email: string): boolean =>
	REGEX.EMAIL.test(email ?? EMPTY_STRING);

export const useForgotPassword = () => {
	const { settings } = useSettings();
	const params = useExtraRequestParameters();
	const successMessagesNLS = useLocalization('SignIn');
	const { showMessage } = useNotifications();
	const trackAuthEvent = useTrackAuthEvent();
	const createPasswordRecoveredParams: GA4PasswordRecoveredPayload = {
		event: GA4_AUTH_EVENTS.PASSWORD_RECOVERED,
		element_name: 'reset password form',
		element_type: 'page',
		element_location: 'reset password page',
		page: typeof window !== 'undefined' ? window.location.href : NOT_APPLICABLE,
		error_type: NOT_APPLICABLE,
		status: 'success',
	};

	/**
	 * Sends a verification code to the given email.
	 * Always returns true — silent fail is intentional for security (email existence is not disclosed).
	 */
	const sendForgotPasswordCode = async (email: string): Promise<boolean> => {
		try {
			await transactionsPerson(true).personUpdatePersonOnUserRegistrationUpdate(
				settings?.storeId ?? '',
				{},
				{ logonId: email.toLowerCase(), resetPassword: 'true', challengeAnswer: '-' },
				params
			);
		} catch {
			// Silent fail: the flow advances regardless to avoid disclosing account existence.
		}
		showMessage(successMessagesNLS.SendVerificationMessage.t(), {
			isGeneric: true,
		});
		return true;
	};
	/**
	 * OOTB compatibility: used by the core ResetPassword page to resend verification codes.
	 * Navigates to /reset-password as before.
	 */
	const forgotPasswordSubmit = async (props: { logonId?: string; email?: string }) => {
		const email = props.logonId || props.email || '';
		await sendForgotPasswordCode(email);
	};
	/**
	 * Submits verification code + new passwords.
	 * Returns errorCode '2190' for invalid code so the component can show inline error (GAP 2 fix).
	 */
	const resetPasswordWithCodeSubmit = async (props: {
		email: string;
		code: string;
		logonPassword: string;
		logonPasswordVerify: string;
	}): Promise<{ success: boolean; errorCode?: string }> => {
		try {
			await transactionsPerson(true).personUpdatePersonOnUserRegistrationUpdate(
				settings?.storeId ?? '',
				{},
				<ComIbmCommerceRestMemberHandlerPersonHandlerUserRegistrationUpdateRequest>{
					logonId: props.email.toLowerCase(),
					resetPassword: 'true',
					xcred_validationCode: props.code,
					logonPassword: props.logonPassword,
					xcred_logonPasswordVerify: props.logonPasswordVerify,
				},
				params
			);
			trackAuthEvent(createPasswordRecoveredParams);
			return { success: true };
		} catch (e) {
			const raw = e as TransactionErrorResponse;
			const errorCode = (raw as any)?.error?.errors?.[0]?.errorCode as string | undefined;
			trackAuthEvent({
				...createPasswordRecoveredParams,
				status: 'error',
				error_type: errorCode || 'error',
			});
			return { success: false, errorCode };
		}
	};
	/**
	 * Notifies the user by email after a successful password reset.
	 * Fire-and-forget — silent fail, no UI impact.
	 */
	const sendPasswordSavedEmail = async (email: string): Promise<void> => {
		try {
			await publicClient.request({
				path: `/store/${settings?.storeId ?? ''}/hdm/mailing/sendPasswordSaved`,
				method: 'POST',
				query: { responseFormat: 'json', langId: '-5' },
				body: { logonId: email.toLowerCase() },
				type: ContentType.Json,
				format: 'json',
				storeId: settings?.storeId ?? '',
			});
		} catch {
			// Silent fail: email notification is non-critical.
		}
	};
	return {
		forgotPasswordSubmit,
		sendForgotPasswordCode,
		resetPasswordWithCodeSubmit,
		sendPasswordSavedEmail,
		initialForgotPassword,
	};
};
